import { Check, HighlightOff } from "@mui/icons-material"
import IconButton from "@mui/material/IconButton"
import React, { ReactNode, useEffect, useState } from "react"

import { create } from "src/helpers/bem"
import { InputField } from "../inputfield"

import styles from "./cardTable.module.scss"

const bem = create(styles, "CardTable")

interface CardTableProps<T> {
  items: T[]
  getKey: (item: T, index: number) => string | number
  onDelete?: (item: T) => void
  onCardClick?: (item: T) => void
  onCardSelect?: (item: T) => void
  onSearch?: (value: string, items: T[]) => T[]
  children: (item: T, index: number) => ReactNode
  emptyMessage?: string
}

export const CardTable = <T,>({
  items,
  getKey,
  onDelete,
  onCardClick,
  onCardSelect,
  onSearch,
  children,
  emptyMessage = "Kein Einträge vorhanden.",
}: CardTableProps<T>) => {
  const [itemsToRender, setItemsToRender] = useState([...items])
  const [searchTerm, setSearchTerm] = useState("")

  useEffect(() => {
    if (onSearch) {
      const filteredItems = onSearch(searchTerm, items)
      setItemsToRender([...filteredItems])
    }
  }, [searchTerm, items, onSearch])

  const renderSearch = () => {
    return (
      <div className={bem("search")}>
        <InputField
          id="card-table-search"
          label="Search"
          value={searchTerm}
          variant="outlined"
          onChange={(e) => {
            setSearchTerm(e.currentTarget.value)
          }}
        />
      </div>
    )
  }

  return (
    <div className={bem()}>
      <div className={bem("header")}>{onSearch && renderSearch()}</div>
      <div className={bem("content")}>
        {itemsToRender.map((item, index) => (
          <div
            key={getKey(item, index)}
            className={bem("item", undefined, onCardClick ? "clickable" : "")}
            role={onCardClick && "button"}
            onClick={() => {
              if (onCardClick) {
                onCardClick(item)
              }
            }}
            onKeyDown={(event) => {
              if (onCardClick && (event.key === "Enter" || event.key === " ")) {
                event.preventDefault()
                onCardClick(item)
              }
            }}
          >
            {(onDelete || onCardSelect) && (
              <div className={bem("cardHeader")}>
                {onDelete && (
                  <IconButton
                    color="error"
                    size="small"
                    onClick={(e) => {
                      e.preventDefault()
                      onDelete(item)
                    }}
                  >
                    <HighlightOff />
                  </IconButton>
                )}
                {onCardSelect && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.preventDefault()
                      onCardSelect(item)
                    }}
                  >
                    <Check />
                  </IconButton>
                )}
              </div>
            )}
            {children(item, index)}
          </div>
        ))}
      </div>
    </div>
  )
}
